import React, { Fragment } from "react";
import { Box, Flex, Grid, Text, useBreakpointValue } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickSyncDownloadHistoryListItem from "@/features/pathpick/features/sync-download-history/components/PathPickSyncDownloadHistoryListItem";
import {
  formatRequestDateGroupLabel,
  groupPathPickRequestsByDate,
} from "@/features/pathpick/utils/request";
import { PathPickRequestItem } from "@/features/pathpick/types";

interface Props {
  items: PathPickRequestItem[];
  onViewSyncedProducts: (item: PathPickRequestItem) => void;
}

// 뷰포트가 좁아지면 모든 칼럼이 최소 너비까지 함께 줄어들고,
// 그 이하에서는 래퍼의 가로 스크롤로 처리한다(우측 잘림 방지).
// 모바일(md 미만)은 칼럼 합이 화면을 한참 넘어 가로 스크롤로만 읽히므로 행을 카드로 그린다.
const TEMPLATE_COLUMNS =
  "180px minmax(120px, 0.8fr) minmax(200px, 1.2fr) minmax(160px, 1fr) 232px";

const COLUMN_KEYS = [
  "kind",
  "requested_at",
  "product_count",
  "status",
  "file_download",
] as const;

const ROW_BORDER = "0.5px solid #D4D4D1";

function PathPickSyncDownloadHistoryList({
  items,
  onViewSyncedProducts,
}: Props) {
  const { t, isKor, i18n } = useI18nHelper();
  // 가로 스크롤은 뷰포트 폭 문제라 UA가 아니라 브레이크포인트로 가른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const layout = isNarrow ? "card" : "row";

  if (items.length === 0) {
    return (
      <CustomNoResults
        title={t("path_pick:sync_download_history.no_results_title")}
      />
    );
  }

  const groups = groupPathPickRequestsByDate(items);

  return (
    <Box overflowX={layout === "row" ? "auto" : undefined}>
      {/* 날짜 헤더와 그 아래 행들이 한 묶음으로 읽히도록 테두리를 표 전체에 하나만 둔다. */}
      <Flex
        flexDirection={"column"}
        minWidth={layout === "row" ? "fit-content" : undefined}
        border={ROW_BORDER}
        borderRadius={"8px"}
        overflow={"hidden"}
        background={"#FFFFFF"}
      >
        {/* 카드는 칸마다 라벨을 붙여 그리므로 칼럼 헤더가 필요 없다. */}
        {layout === "row" && (
          <Grid
            templateColumns={TEMPLATE_COLUMNS}
            gap={"16px"}
            alignItems={"center"}
            padding={"10px 16px"}
            background={"#F7F9FA"}
            borderBottom={ROW_BORDER}
          >
            {COLUMN_KEYS.map((key) => (
              <Text
                key={key}
                textStyle={"body-sm-medium"}
                color={"#8F8F8C"}
                textAlign={key === "file_download" ? "right" : undefined}
              >
                {t(`path_pick:sync_download_history.column.${key}`)}
              </Text>
            ))}
          </Grid>
        )}

        {groups.map((group, groupIndex) => (
          <Fragment key={group.dateKey}>
            {/* 그룹 헤더는 컬럼 경계를 무시하고 전체 폭을 가로지른다. */}
            <Flex
              gap={"8px"}
              alignItems={"center"}
              padding={"8px 16px"}
              background={"#FAFAF9"}
                borderTop={groupIndex > 0 ? ROW_BORDER : undefined}
              borderBottom={ROW_BORDER}
            >
              <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                {formatRequestDateGroupLabel(
                  group.dateKey,
                  isKor,
                  i18n.language
                )}
              </Text>
              <PathPickBadge
                label={t(
                  "path_pick:sync_download_history.group_request_count",
                  {
                    count: group.items.length,
                  }
                )}
                colorVariant={"gray"}
                size={"medium"}
              />
            </Flex>
            {group.items.map((item, itemIndex) => (
              <PathPickSyncDownloadHistoryListItem
                key={item.id}
                item={item}
                layout={layout}
                templateColumns={TEMPLATE_COLUMNS}
                // 그룹의 마지막 행 아래 선은 다음 그룹 헤더가 그린다.
                hasBorderBottom={itemIndex < group.items.length - 1}
                onViewSyncedProducts={onViewSyncedProducts}
              />
            ))}
          </Fragment>
        ))}
      </Flex>
    </Box>
  );
}

export default PathPickSyncDownloadHistoryList;
